import { defineStore } from 'pinia'
import { journalsApi } from '../api/journalsApi'
import { useErrorHandler } from '../composables/useErrorHandler'
import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import type { JournalItem, PaginatedResponse } from '../types/journal.types'
import { useJournalsListFilterStore } from './journalsListFilterStore'

interface JournalsResponse extends PaginatedResponse<JournalItem> {}

export const useJournalsListStore = defineStore('journalsList', {
  state: () => ({
    allJournals: [] as JournalItem[],
    responseData: null as JournalsResponse | null,
    totalJournalsCount: 0,
    isFetching: false,
    error: null as unknown | null,
    searchQuery: '',
    itemsPerPage: DEFAULT_PAGE_SIZE,
    page: 1,
    cursor: undefined as string | undefined,
    nextCursor: undefined as string | undefined,
    prevCursor: undefined as string | undefined,
    sortBy: undefined as string | undefined,
    orderBy: undefined as ('asc' | 'desc') | undefined,
  }),

  getters: {
    journals(state): JournalItem[] {
      return state.allJournals
    },

    totalItems(state): number {
      return state.totalJournalsCount || state.allJournals.length
    },
  },

  actions: {
    setSearchQuery(query: string) {
      this.searchQuery = query
    },

    setItemsPerPage(itemsPerPage: number) {
      this.itemsPerPage = itemsPerPage
    },

    setPage(page: number) {
      this.page = page
    },

    setCursor(cursor: string | undefined) {
      this.cursor = cursor
    },

    setSort(sortBy?: string, orderBy?: 'asc' | 'desc') {
      this.sortBy = sortBy
      this.orderBy = orderBy
    },

    /** Сбрасывает пагинацию и накопленные данные перед запросом с новыми параметрами. */
    resetPagination() {
      this.page = 1
      this.cursor = undefined
      this.nextCursor = undefined
      this.prevCursor = undefined
      this.allJournals = []
      this.totalJournalsCount = 0
    },

    async fetchJournals(): Promise<void> {
      this.isFetching = true
      this.error = null

      const { handleError } = useErrorHandler()

      try {
        const filterStore = useJournalsListFilterStore()

        const params: Record<string, string | number | string[] | undefined> = {
          search: this.searchQuery || undefined,
          page: this.page,
          page_size: this.itemsPerPage,
          cursor: this.cursor,
        }

        if (filterStore.filters.departments?.length) {
          params.departments = filterStore.filters.departments.join(',')
        }
        if (filterStore.filters.responsible_employees?.length) {
          params.responsible_employees = filterStore.filters.responsible_employees.join(',')
        }
        if (filterStore.filters.created_by?.length) {
          params.creators = filterStore.filters.created_by.join(',')
        }
        if (filterStore.filters.created_at?.from) {
          params.created_after = filterStore.filters.created_at.from
        }
        if (filterStore.filters.created_at?.to) {
          params.created_before = filterStore.filters.created_at.to
        }

        const data = await journalsApi.getJournals(params)

        this.responseData = data
        this.allJournals = data.results
        this.totalJournalsCount = data.count ?? data.results.length
        this.nextCursor = data.next || undefined
        this.prevCursor = data.previous || undefined
      } catch (e) {
        this.error = e
        console.error('JOURNALS_FETCH_ERROR', e)
        handleError(e, 'Ошибка загрузки журналов')
      } finally {
        this.isFetching = false
      }
    },
  },
})
